import { assign, get } from 'lodash'
import { structure2mesh } from '@wix/thunderbolt-mesh'
import type {
	Structure,
	Style,
	Mesh,
	MeshHierarchyStructure,
	MeshOptions,
} from '@wix/thunderbolt-becky-types'
import type { BeckyFunctionLibrary, DataForMeshCalculation, MeshResult, OverridesData } from '../../types'
import { POSITION_ABSOLUTE_IN_MESH, skinPartId } from './meshConstants'
import { getMeshOverrides } from './meshOverrides'
import { getMeshStyles } from './meshStyle'
import type { AutoHeightComponents, MeshComponent, MeshSelectorsOptions } from '../../types/mesh'

export const getStylesAndWedgesAndRotated = (
	compId: string,
	meshData: Mesh,
	autoHeightComps: AutoHeightComponents,
	options?: MeshSelectorsOptions
): MeshResult => {
	const children = get(meshData, 'structure.children[0].children[0].children', []) as Array<MeshHierarchyStructure>
	const wedges = children.map((child) => child.id).filter((id) => id.includes('wedge'))
	const rotatedComponents = children
		.filter((child) => child.id.includes('rotated-wrapper'))
		.map((rotatedWrapper) => rotatedWrapper.children[0].id)
	const stylesMap = getMeshStyles(compId, meshData.styles, rotatedComponents, autoHeightComps, options)
	if (stylesMap?.repeaterStylesMap) {
		const { repeaterStylesMap } = stylesMap
		delete stylesMap.repeaterStylesMap
		return { stylesMap: stylesMap.stylesMap, wedges, rotatedComponents, repeaterStylesMap } as MeshResult
	}

	return { stylesMap, wedges, rotatedComponents }
}

const getMeshContainer = (
	compMeshData: DataForMeshCalculation,
	components: Array<MeshComponent>,
	overridesData: OverridesData
) => {
	const { compStructure, compData, dataForColumn = {} } = compMeshData
	const { layout, metaData: compMetaData } = compStructure
	const { props: compProp = {}, style = {}, data = {} } = compData
	const compSkin = (style as Style).skin
	const skinName = (compSkin || compStructure.skin) as string
	const filteredComps = components.filter((child) => !child.isFixed)

	const width = layout.width
	const height = layout.height

	return {
		width,
		height,
		id: `${compStructure.id}${skinPartId}`,
		rootId: compStructure.id,
		components: filteredComps,
		adjustingComp: null,
		...getMeshOverrides({
			...overridesData,
			compProp,
			compData: data,
			dataForColumn,
			componentType: compStructure.componentType,
			compId: compStructure.id,
			compMetaData,
			skinName,
		}),
	}
}

const removeMeshGridWrapper = (meshData: Mesh): Mesh => {
	const gridWrapperId = get(meshData.structure, ['children', '0', 'id'])

	if (!gridWrapperId) {
		// empty container
		return meshData
	}

	assign(meshData.styles[meshData.structure.id], meshData.styles[gridWrapperId])
	delete meshData.styles[gridWrapperId]

	return meshData
}

export const getMeshData = (
	compMeshData: DataForMeshCalculation,
	childComponents: Array<MeshComponent>,
	overridesData: OverridesData,
	meshOptions: MeshOptions = {}
): Mesh => {
	const container = getMeshContainer(compMeshData, childComponents, overridesData)

	// @ts-ignore - Remove once santa-mesh is updated
	const meshData = structure2mesh(container, {
		...meshOptions,
		isRepeatSupported: true,
	})

	return removeMeshGridWrapper(meshData)
}

export const getMeshComponents = (
	compId: string,
	structure: Structure,
	absoluteComps: Array<string>
): Array<MeshComponent> => {
	const compStructure = structure[compId]

	return (compStructure.components || []).map((childCompId) => {
		const childStructure = structure[childCompId]
		const { x, y, width, height, docked, fixedPosition, rotationInDegrees } = childStructure.layout

		return {
			id: childCompId,
			type: childStructure.componentType,
			left: x,
			top: y,
			width,
			height,
			docked: docked || {},
			isFixed: Boolean(fixedPosition),
			rotationInDegrees,
			absolute:
				POSITION_ABSOLUTE_IN_MESH.includes(childStructure.componentType) || absoluteComps.includes(childCompId),
		} as MeshComponent
	})
}

const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b)

const cache = new WeakMap<DataForMeshCalculation['compStructure'], MeshResult>()

export const getMeshResult: BeckyFunctionLibrary['getMeshResult'] = (
	compMeshData,
	overridesData,
	absoluteComps,
	autoHeightComps
) => {
	const compId = compMeshData.compStructure.id
	const compStructure = compMeshData.compStructure

	const childComponents = getMeshComponents(compId, compMeshData.compAndChildrenStructure!, absoluteComps)
	const fixedComponents = childComponents.filter((comp) => comp.isFixed).map((comp) => comp.id)

	const meshResult: MeshResult = {
		fixedComponents,
		...getStylesAndWedgesAndRotated(
			compId,
			getMeshData(compMeshData, childComponents, overridesData),
			autoHeightComps,
			{
				isChildOfRepeater: compMeshData.isChildOfRepeater,
				isMeshCatharsisExperimentOpen: compMeshData.isMeshCatharsisExperimentOpen,
			}
		),
	}

	const prevMeshResult = cache.get(compStructure)
	if (!prevMeshResult || !isEqual(meshResult, prevMeshResult)) {
		cache.set(compStructure, meshResult)
		return meshResult
	}

	return prevMeshResult
}
